import React, { useEffect, useRef } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageHeader } from "#/features/global-ui/page-header"
import { Title } from "#/services/page-title/Title"
import { Box, Chip, Stack, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import CollectiveGrid from "#/components/carrot-collective/CollectiveGrid"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { useJourney } from "#/components/carrot-plans/hooks/useJourney"

export function CarrotCollectiveLandingPage(): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const { mutate: addMemberAction } = useMemberActionTracking()
  const { resolvedJourney, journeyDataIsLoaded } = useJourney()
  const hasTracked = useRef(false)

  useEffect(() => {
    if (!journeyDataIsLoaded || hasTracked.current) return
    addMemberAction({
      memberActionTaken: MemberActions.CARROT_COLLECTIVE_VIEWED_PAGE,
      metaData: {
        memberJourney: resolvedJourney
      }
    })
    hasTracked.current = true
  }, [journeyDataIsLoaded, resolvedJourney, addMemberAction])

  return (
    <>
      <Title title={intl.formatMessage({ defaultMessage: "Carrot Collective" })} />
      <PageLayout header={<PageHeader pageTitle={<FormattedMessage defaultMessage="Carrot Collective" />} />}>
        <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
          <CollectiveBanner />
          <CollectiveGrid />
        </Stack>
      </PageLayout>
    </>
  )
}

function CollectiveBanner(): JSX.Element {
  const theme = useTheme()

  return (
    <Tile variant="primary" color="blue" sx={{ overflow: "hidden", padding: 0 }}>
      <Box display="flex" flexDirection={{ xs: "column", md: "row" }} position="relative">
        <Box flex={{ xs: "1", md: "0 0 67%" }} padding={theme.spacing(theme.tokens.spacing.lg)}>
          <Stack spacing={theme.spacing(theme.tokens.spacing.md)} alignItems="start">
            <Chip
              label={<FormattedMessage defaultMessage="Curated for you" />}
              sx={{ backgroundColor: theme.palette.success.contrastText }}
            />
            <Typography variant="h3" component="h2">
              <FormattedMessage defaultMessage="Exclusive discounts" />
            </Typography>
            <Typography variant="body1">
              <FormattedMessage defaultMessage="Access exclusive discounts on partner products and services — all carefully selected and clinically vetted to support you through every step of your fertility, family-building, or hormonal health journey." />
            </Typography>
          </Stack>
        </Box>
        <FeaturedImage />
      </Box>
    </Tile>
  )
}

function FeaturedImage(): JSX.Element {
  const intl = useIntl()
  return (
    <Box
      sx={{
        position: { xs: "relative", md: "absolute" },
        insetInlineEnd: { md: 0 },
        insetBlockStart: { md: 0 },
        inlineSize: { xs: "100%", md: "275px" },
        maxInlineSize: { md: "33%" },
        blockSize: { xs: "auto", md: "100%" },
        order: { xs: -1, md: 0 },
        display: { xs: "none", md: "block" },
        overflow: "hidden"
      }}
    >
      <Box
        component="img"
        src={"/images/oura-ring.jpeg"}
        aria-label={intl.formatMessage({ defaultMessage: "Oura ring still life" })}
        sx={{
          inlineSize: "100%",
          blockSize: "100%",
          objectFit: "cover"
        }}
      />
    </Box>
  )
}
